import { EncodingOperation, EncoderToolType } from '../types';

export interface JwtTokenConfiguration {
  id: string;
  operation: 'encode' | 'decode' | 'verify' | 'generate';
  payload?: Record<string, any>;
  secret?: string;
  algorithm?: 'HS256' | 'HS384' | 'HS512' | 'RS256' | 'RS384' | 'RS512';
  expiresIn?: string | number;
  issuer?: string;
  audience?: string;
  subject?: string;
  token?: string;
  publicKey?: string;
  privateKey?: string;
  processedAt: Date;
}

export interface JwtTokenInfo {
  header: Record<string, any>;
  payload: Record<string, any>;
  signature: string;
  isValid: boolean;
  errors: string[];
  expirationTime?: Date;
  issuedAt?: Date;
  notBefore?: Date;
}

export interface JwtValidationRule {
  algorithm: string[];
  maxExpiration: number;
  minSecretLength: number;
  allowedIssuers?: string[];
  allowedAudiences?: string[];
}

export class JwtTokenService {
  private static readonly DEFAULT_CONFIG: Partial<JwtTokenConfiguration> = {
    algorithm: 'HS256',
    expiresIn: '1h',
    processedAt: new Date()
  };

  private static readonly SUPPORTED_ALGORITHMS = ['HS256', 'HS384', 'HS512', 'RS256', 'RS384', 'RS512'];
  private static readonly DEFAULT_VALIDATION: JwtValidationRule = {
    algorithm: this.SUPPORTED_ALGORITHMS,
    maxExpiration: 24 * 60 * 60 * 1000, // 24 hours
    minSecretLength: 32
  };

  static createConfiguration(
    operation: 'encode' | 'decode' | 'verify' | 'generate',
    options: Partial<JwtTokenConfiguration> = {}
  ): JwtTokenConfiguration {
    return {
      id: `jwt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      operation,
      ...this.DEFAULT_CONFIG,
      ...options,
      processedAt: new Date()
    };
  }

  static async encode(config: JwtTokenConfiguration): Promise<EncodingOperation> {
    try {
      if (!config.payload) {
        throw new Error('Payload is required for JWT encoding');
      }

      if (!config.secret && !config.privateKey) {
        throw new Error('Secret (for symmetric) or private key (for asymmetric) is required');
      }

      const header = {
        alg: config.algorithm || 'HS256',
        typ: 'JWT'
      };

      // Build payload with standard claims
      const payload: Record<string, any> = {
        ...config.payload,
        iat: Math.floor(Date.now() / 1000), // Issued at
        exp: this.calculateExpirationTime(config.expiresIn),
        iss: config.issuer,
        aud: config.audience,
        sub: config.subject
      };

      // Remove undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      // Create JWT
      const token = await this.createJwt(header, payload, config);

      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'encode',
        inputData: JSON.stringify(config.payload || {}),
        outputData: token,
        timestamp: config.processedAt,
        status: 'success',
        metadata: {
          algorithm: config.algorithm,
          expiresIn: config.expiresIn,
          issuer: config.issuer,
          audience: config.audience,
          tokenLength: token.length
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'encode',
        inputData: JSON.stringify(config.payload || {}),
        timestamp: config.processedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Encoding failed'
      };
    }
  }

  static async decode(config: JwtTokenConfiguration): Promise<EncodingOperation> {
    try {
      if (!config.token) {
        throw new Error('Token is required for JWT decoding');
      }

      const tokenInfo = await this.parseToken(config.token, config);

      if (!tokenInfo.isValid) {
        throw new Error(`Invalid JWT: ${tokenInfo.errors.join(', ')}`);
      }

      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'decode',
        inputData: config.token || '',
        outputData: JSON.stringify(tokenInfo.payload),
        timestamp: config.processedAt,
        status: 'success',
        metadata: {
          header: tokenInfo.header,
          payload: tokenInfo.payload,
          algorithm: tokenInfo.header.alg,
          issuer: tokenInfo.payload.iss,
          audience: tokenInfo.payload.aud,
          subject: tokenInfo.payload.sub,
          expirationTime: tokenInfo.expirationTime,
          issuedAt: tokenInfo.issuedAt,
          tokenLength: (config.token || '').length
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'decode',
        inputData: config.token || '',
        timestamp: config.processedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Decoding failed'
      };
    }
  }

  static async verify(config: JwtTokenConfiguration): Promise<EncodingOperation> {
    try {
      if (!config.token) {
        throw new Error('Token is required for JWT verification');
      }

      if (!config.secret && !config.publicKey && !config.privateKey) {
        throw new Error('Secret (for symmetric), public key, or private key (for asymmetric) is required');
      }

      const tokenInfo = await this.parseToken(config.token, config);

      if (!tokenInfo.isValid) {
        return {
          id: config.id,
          toolType: EncoderToolType.JWT_TOKEN,
          operationType: 'decode',
          inputData: config.token,
          timestamp: config.processedAt,
          status: 'error',
          errorMessage: `Invalid JWT: ${tokenInfo.errors.join(', ')}`,
          metadata: {
            verificationErrors: tokenInfo.errors
          }
        };
      }

      // Additional verification using secret, public key, or private key
      const isSignatureValid = await this.verifySignature(config.token, config);

      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'decode',
        inputData: config.token || '',
        outputData: JSON.stringify({
          ...tokenInfo.payload,
          signatureValid: isSignatureValid,
          verificationStatus: isSignatureValid ? 'success' : 'failed'
        }),
        timestamp: config.processedAt,
        status: 'success',
        metadata: {
          header: tokenInfo.header,
          payload: tokenInfo.payload,
          algorithm: tokenInfo.header.alg,
          issuer: tokenInfo.payload.iss,
          audience: tokenInfo.payload.aud,
          subject: tokenInfo.payload.sub,
          expirationTime: tokenInfo.expirationTime,
          issuedAt: tokenInfo.issuedAt,
          signatureValid: isSignatureValid
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'decode',
        inputData: config.token || '',
        timestamp: config.processedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Verification failed'
      };
    }
  }

  static async generate(config: JwtTokenConfiguration): Promise<EncodingOperation> {
    try {
      // Generate a secure random secret if not provided
      const secret = config.secret || this.generateSecret();

      // Generate a random payload if not provided
      const payload = config.payload || {
        userId: Math.random().toString(36).substr(2, 9),
        sessionId: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now()
      };

      // Create configuration with generated values
      const fullConfig = {
        ...config,
        secret,
        payload
      };

      // Encode the token
      const encodeResult = await this.encode(fullConfig);
      
      if (encodeResult.status !== 'success') {
        return encodeResult;
      }

      return {
        ...encodeResult,
        operationType: 'encode',
        metadata: {
          ...encodeResult.metadata,
          generatedSecret: config.secret ? undefined : secret,
          generatedPayload: config.payload ? undefined : payload
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.JWT_TOKEN,
        operationType: 'encode',
        inputData: JSON.stringify(config.payload || {}),
        timestamp: config.processedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Generation failed'
      };
    }
  }

  static async parseToken(token: string, config?: Partial<JwtTokenConfiguration>): Promise<JwtTokenInfo> {
    const errors: string[] = [];
    let header: Record<string, any> = {};
    let payload: Record<string, any> = {};
    let signature = '';

    try {
      // Split token into parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        errors.push('Invalid JWT format (must have 3 parts separated by dots)');
        return { header, payload, signature, isValid: false, errors };
      }

      // Decode header
      try {
        header = JSON.parse(this.base64UrlDecode(parts[0]));
      } catch (error) {
        errors.push('Invalid header encoding');
      }

      // Decode payload
      try {
        payload = JSON.parse(this.base64UrlDecode(parts[1]));
      } catch (error) {
        errors.push('Invalid payload encoding');
      }

      // Get signature
      signature = parts[2];

      // Validate structure
      if (!header.alg) {
        errors.push('Missing algorithm in header');
      }

      if (!header.typ || header.typ !== 'JWT') {
        errors.push('Invalid or missing type in header');
      }

      // Validate claims
      this.validateClaims(payload, errors, config);

      // Check if token has expired
      if (payload.exp && payload.exp < Date.now() / 1000) {
        errors.push('Token has expired');
      }

      // Check not before claim
      if (payload.nbf && payload.nbf > Date.now() / 1000) {
        errors.push('Token is not yet valid');
      }

      return {
        header,
        payload,
        signature,
        isValid: errors.length === 0,
        errors,
        expirationTime: payload.exp ? new Date(payload.exp * 1000) : undefined,
        issuedAt: payload.iat ? new Date(payload.iat * 1000) : undefined,
        notBefore: payload.nbf ? new Date(payload.nbf * 1000) : undefined
      };
    } catch (error) {
      errors.push('Failed to parse token');
      return { header, payload, signature, isValid: false, errors };
    }
  }

  static async validateToken(token: string, config?: Partial<JwtTokenConfiguration>): Promise<{ isValid: boolean; errors: string[] }> {
    const tokenInfo = await this.parseToken(token, config);
    return {
      isValid: tokenInfo.isValid,
      errors: tokenInfo.errors
    };
  }

  static validateSecret(secret: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!secret || secret.trim().length === 0) {
      errors.push('Secret cannot be empty');
      return { isValid: false, errors };
    }

    if (secret.length < this.DEFAULT_VALIDATION.minSecretLength) {
      errors.push(`Secret must be at least ${this.DEFAULT_VALIDATION.minSecretLength} characters long`);
    }

    // Check for common weak secrets
    const weakSecrets = ['password', 'secret', '123456', 'qwerty', 'admin', 'test'];
    if (weakSecrets.includes(secret.toLowerCase())) {
      errors.push('Secret is too common and weak');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static validateConfiguration(config: JwtTokenConfiguration): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate algorithm
    if (config.algorithm && !this.SUPPORTED_ALGORITHMS.includes(config.algorithm)) {
      errors.push(`Unsupported algorithm: ${config.algorithm}. Supported: ${this.SUPPORTED_ALGORITHMS.join(', ')}`);
    }

    // Validate expiration
    if (config.expiresIn) {
      const expirationMs = this.parseExpiration(config.expiresIn);
      if (expirationMs > this.DEFAULT_VALIDATION.maxExpiration) {
        errors.push(`Expiration time exceeds maximum (${this.DEFAULT_VALIDATION.maxExpiration / 1000 / 60 / 60} hours)`);
      }
    }

    // Validate secret for symmetric algorithms
    if (config.algorithm && config.algorithm.startsWith('HS') && !config.secret) {
      errors.push('Secret is required for symmetric algorithms');
    }

    // Validate keys for asymmetric algorithms
    if (config.algorithm && config.algorithm.startsWith('RS')) {
      if (!config.privateKey && !config.publicKey) {
        errors.push('Private key or public key is required for asymmetric algorithms');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static async getTokenInfo(token: string): Promise<JwtTokenInfo | null> {
    try {
      return await this.parseToken(token);
    } catch (error) {
      return null;
    }
  }

  static async isTokenExpired(token: string): Promise<boolean> {
    const tokenInfo = await this.getTokenInfo(token);
    if (!tokenInfo || !tokenInfo.expirationTime) {
      return false;
    }
    return tokenInfo.expirationTime < new Date();
  }

  static async refreshTokenIfNeeded(token: string, config: JwtTokenConfiguration): Promise<string | null> {
    const tokenInfo = await this.getTokenInfo(token);

    if (!tokenInfo || !tokenInfo.expirationTime) {
      return null;
    }

    // Check if token expires within the next 5 minutes
    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
    if (tokenInfo.expirationTime > fiveMinutesFromNow) {
      return null; // Token is still valid
    }

    // Token needs refresh
    if (config.payload) {
      const newPayload = { ...config.payload };
      delete newPayload.exp; // Remove old expiration
      delete newPayload.iat; // Remove old issued at

      const newConfig = { ...config, payload: newPayload };
      return await this.createJwt(tokenInfo.header, newPayload, newConfig);
    }

    return null;
  }

  static createTokenPair(_config?: Partial<JwtTokenConfiguration>): { accessToken: string; refreshToken: string } {
    // This would normally involve async operations
    // For now, return mock tokens
    const accessToken = 'mock-access-token';
    const refreshToken = 'mock-refresh-token';

    return { accessToken, refreshToken };
  }

  
  // Private helper methods

  private static calculateExpirationTime(expiresIn?: string | number): number {
    if (!expiresIn) {
      return Math.floor(Date.now() / 1000) + 3600; // Default 1 hour
    }

    if (typeof expiresIn === 'number') {
      return Math.floor(Date.now() / 1000) + expiresIn;
    }

    // Parse time strings like '1h', '30m', '7d'
    const match = expiresIn.match(/^(\d+)([smhd])$/);
    if (!match) {
      return Math.floor(Date.now() / 1000) + 3600; // Default fallback
    }

    const value = parseInt(match[1], 10);
    const unit = match[2];

    const multipliers: Record<string, number> = {
      s: 1,
      m: 60,
      h: 3600,
      d: 86400
    };

    return Math.floor(Date.now() / 1000) + (value * (multipliers[unit] || 1));
  }

  private static parseExpiration(expiresIn: string | number): number {
    if (typeof expiresIn === 'number') {
      return expiresIn;
    }

    const match = expiresIn.toString().match(/^(\d+)([smhd])$/);
    if (!match) {
      return 3600; // Default 1 hour
    }

    const value = parseInt(match[1], 10);
    const unit = match[2];

    const multipliers: Record<string, number> = {
      s: 1,
      m: 60,
      h: 3600,
      d: 86400
    };

    return value * (multipliers[unit] || 1);
  }

  private static validateClaims(
    payload: Record<string, any>,
    errors: string[],
    config?: Partial<JwtTokenConfiguration>
  ): void {
    // Validate issuer if specified in config
    if (config?.issuer && payload.iss && payload.iss !== config.issuer) {
      errors.push(`Invalid issuer: ${payload.iss}`);
    }

    // Validate audience if specified in config
    if (config?.audience && payload.aud) {
      const audiences = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
      const hasValidAudience = audiences.includes(config.audience);
      if (!hasValidAudience) {
        errors.push(`Invalid audience: ${audiences.join(', ')}`);
      }
    }

    // Validate subject if required
    if (config?.subject && !payload.sub) {
      errors.push('Subject claim is required');
    }
  }

  private static async createJwt(
    header: Record<string, any>,
    payload: Record<string, any>,
    config: JwtTokenConfiguration
  ): Promise<string> {
    const encodedHeader = this.base64UrlEncode(JSON.stringify(header));
    const encodedPayload = this.base64UrlEncode(JSON.stringify(payload));

    // Create the data to be signed
    const data = `${encodedHeader}.${encodedPayload}`;

    if (config.algorithm && config.algorithm.startsWith('HS')) {
      // Use Web Crypto API for HMAC signature
      const encoder = new TextEncoder();
      const keyData = encoder.encode(config.secret!);
      const dataBuffer = encoder.encode(data);

      // Import the secret key
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: `SHA-${config.algorithm.slice(-3)}` },
        false,
        ['sign']
      );

      // Sign the data
      const signatureBuffer = await crypto.subtle.sign(
        'HMAC',
        cryptoKey,
        dataBuffer
      );

      // Convert signature to base64url
      const signature = this.uint8ArrayToBase64Url(new Uint8Array(signatureBuffer));

      return `${encodedHeader}.${encodedPayload}.${signature}`;
    }

    // For asymmetric algorithms, use RSA signature
    if (config.algorithm && config.algorithm.startsWith('RS')) {
      try {
        // Create buffer for signing
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(data);

        let privateKey: CryptoKey;

        // Import the PEM private key
        try {
          privateKey = await this.importRSAPrivateKey(config.secret!, config.algorithm);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error('RSA private key import error:', errorMessage);
          throw new Error(`Invalid RSA private key format: ${errorMessage}. Please provide a valid PEM-formatted private key for ${config.algorithm} algorithm.`);
        }

        // Sign the data using RSA PKCS#1 v1.5 (standard for JWT)
        const signatureBuffer = await crypto.subtle.sign(
          { name: 'RSASSA-PKCS1-v1_5' },
          privateKey,
          dataBuffer
        );

        // Convert signature to base64url
        const signature = this.uint8ArrayToBase64Url(new Uint8Array(signatureBuffer));

        return `${encodedHeader}.${encodedPayload}.${signature}`;
      } catch (error) {
        // If all attempts fail, cannot proceed with RSA signing
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('RSA signing error:', errorMessage);
        throw new Error(`Failed to create RSA signature: ${errorMessage}. Please provide a valid PEM-formatted private key or a simple secret for development.`);
      }
    }

    // Fallback to mock signature for unknown algorithms
    const signature = this.base64UrlEncode('mock-signature');
    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  private static async importRSAPrivateKey(pemKey: string, algorithm: string): Promise<CryptoKey> {
    // Remove PEM headers and footers
    const pemHeader = "-----BEGIN PRIVATE KEY-----";
    const pemFooter = "-----END PRIVATE KEY-----";
    const pemHeaderRSA = "-----BEGIN RSA PRIVATE KEY-----";

    let pem = pemKey.trim();

    if (pem.includes(pemHeader)) {
      // PKCS#8 format - supported by Web Crypto API
      pem = pem.replace(pemHeader, '').replace(pemFooter, '').replace(/\s/g, '');

      try {
        // Decode base64 to binary
        const binaryString = atob(pem);
        const binaryDer = Uint8Array.from(binaryString, c => c.charCodeAt(0));

        // Import the PKCS#8 private key
        return await crypto.subtle.importKey(
          'pkcs8',
          binaryDer,
          {
            name: 'RSASSA-PKCS1-v1_5',
            hash: `SHA-${algorithm.slice(-3)}`
          },
          true, // Make it extractable so we can export the public key
          ['sign']
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('RSA private key format error:', errorMessage);
        throw new Error(`Invalid RSA private key format: ${errorMessage}. Please provide a valid PEM-formatted private key.`);
      }
    } else if (pem.includes(pemHeaderRSA)) {
      // Traditional RSA format - not directly supported by Web Crypto API
      throw new Error('Traditional RSA private key format is not supported. Please use PKCS#8 format (-----BEGIN PRIVATE KEY-----).');
    } else {
      // No PEM headers - for encode mode, generate deterministic key; for verify mode, this is an error
      throw new Error('Invalid RSA private key format. Please provide a valid PEM-formatted private key starting with "-----BEGIN PRIVATE KEY-----" or "-----BEGIN RSA PRIVATE KEY-----".');
    }
  }

  
  private static async importRSAPublicKey(pemKey: string, algorithm: string): Promise<CryptoKey> {
    // Remove PEM headers and footers
    const pemHeader = "-----BEGIN PUBLIC KEY-----";
    const pemFooter = "-----END PUBLIC KEY-----";
    const pemHeaderRSA = "-----BEGIN RSA PUBLIC KEY-----";

    let pem = pemKey.trim();

    if (pem.includes(pemHeader)) {
      // SubjectPublicKeyInfo format - supported by Web Crypto API
      pem = pem.replace(pemHeader, '').replace(pemFooter, '').replace(/\s/g, '');

      try {
        // Decode base64 to binary
        const binaryString = atob(pem);
        const binaryDer = Uint8Array.from(binaryString, c => c.charCodeAt(0));

        // Import the public key
        return await crypto.subtle.importKey(
          'spki',
          binaryDer,
          {
            name: 'RSASSA-PKCS1-v1_5',
            hash: `SHA-${algorithm.slice(-3)}`
          },
          false,
          ['verify']
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('RSA public key format error:', errorMessage);
        throw new Error(`Invalid RSA public key format: ${errorMessage}. Please provide a valid PEM-formatted public key.`);
      }
    } else if (pem.includes(pemHeaderRSA)) {
      // Traditional RSA public key format - not directly supported by Web Crypto API
      throw new Error('Traditional RSA public key format is not supported. Please use SubjectPublicKeyInfo format (-----BEGIN PUBLIC KEY-----).');
    } else {
      // No PEM headers - invalid format
      throw new Error('Invalid RSA public key format. Please provide a valid PEM-formatted public key starting with "-----BEGIN PUBLIC KEY-----" or "-----BEGIN RSA PUBLIC KEY-----".');
    }
  }

  
  
  private static uint8ArrayToBase64Url(bytes: Uint8Array): string {
    // Convert to base64 then to base64url
    const binaryString = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
    const base64 = btoa(binaryString);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private static async verifySignature(token: string, config: JwtTokenConfiguration): Promise<boolean> {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      const [headerB64, payloadB64, signatureB64] = parts;

      // Recreate the data that was signed
      const data = `${headerB64}.${payloadB64}`;

      if (config.algorithm && config.algorithm.startsWith('HS')) {
        // Use Web Crypto API for HMAC verification
        const encoder = new TextEncoder();
        const keyData = encoder.encode(config.secret!);
        const dataBuffer = encoder.encode(data);

        // Import the secret key
        const cryptoKey = await crypto.subtle.importKey(
          'raw',
          keyData,
          { name: 'HMAC', hash: `SHA-${config.algorithm.slice(-3)}` },
          false,
          ['verify']
        );

        // Convert signature from base64url to raw
        const signature = this.base64UrlDecodeToUint8Array(signatureB64);

        // Verify the signature
        const isValid = await crypto.subtle.verify(
          'HMAC',
          cryptoKey,
          signature as unknown as BufferSource,
          dataBuffer
        );

        return isValid;
      }

      // For asymmetric algorithms, verify with public key or extract from private key
      if (config.algorithm && config.algorithm.startsWith('RS')) {
        try {
          // Create buffer for verification
          const encoder = new TextEncoder();
          const dataBuffer = encoder.encode(data);

          let publicKey: CryptoKey;

          // Try public key first if provided
          if (config.publicKey) {
            publicKey = await this.importRSAPublicKey(config.publicKey, config.algorithm);
          } else if (config.privateKey) {
            // Import the private key and extract the public key
            const privateKey = await this.importRSAPrivateKey(config.privateKey, config.algorithm);

            // Export the public key from the private key
            const publicKeyJwk = await crypto.subtle.exportKey('jwk', privateKey);

            // Remove the private key fields and set correct key_ops for verification
            const { d, p, q, dp, dq, qi, key_ops, ...publicKeyFields } = publicKeyJwk;
            const publicKeyJwkForVerification = {
              ...publicKeyFields,
              key_ops: ['verify'] // Explicitly set verify operations for public key
            };

            // Import the public key for verification
            publicKey = await crypto.subtle.importKey(
              'jwk',
              publicKeyJwkForVerification,
              {
                name: 'RSASSA-PKCS1-v1_5',
                hash: `SHA-${config.algorithm.slice(-3)}`
              },
              false,
              ['verify']
            );
          } else if (config.secret) {
            // Support legacy secret field for private key
            const privateKey = await this.importRSAPrivateKey(config.secret, config.algorithm);

            // Export the public key from the private key
            const publicKeyJwk = await crypto.subtle.exportKey('jwk', privateKey);

            // Remove the private key fields and set correct key_ops for verification
            const { d, p, q, dp, dq, qi, key_ops, ...publicKeyFields } = publicKeyJwk;
            const publicKeyJwkForVerification = {
              ...publicKeyFields,
              key_ops: ['verify'] // Explicitly set verify operations for public key
            };

            // Import the public key for verification
            publicKey = await crypto.subtle.importKey(
              'jwk',
              publicKeyJwkForVerification,
              {
                name: 'RSASSA-PKCS1-v1_5',
                hash: `SHA-${config.algorithm.slice(-3)}`
              },
              false,
              ['verify']
            );
          } else {
            return false;
          }

          // Convert signature from base64url to raw
          const signature = this.base64UrlDecodeToUint8Array(signatureB64);

          // Verify the signature using RSA PKCS#1 v1.5
          const isValid = await crypto.subtle.verify(
            { name: 'RSASSA-PKCS1-v1_5' },
            publicKey,
            signature as unknown as BufferSource,
            dataBuffer
          );

          return isValid;
        } catch (error) {
          // If key import fails, cannot verify RSA signature
          console.error('RSA signature verification error:', error);
          return false;
        }
      }

      return false;
    } catch (error) {
      console.error('Signature verification error:', error);
      return false;
    }
  }

  private static base64UrlDecodeToUint8Array(base64url: string): Uint8Array {
    // Add padding if needed
    while (base64url.length % 4) {
      base64url += '=';
    }

    // Replace URL-safe characters
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

    // Decode to binary string
    const binaryString = atob(base64);

    // Convert to Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }

  private static base64UrlEncode(str: string): string {
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private static base64UrlDecode(str: string): string {
    // Add padding if needed
    while (str.length % 4) {
      str += '=';
    }

    // Replace URL-safe characters
    str = str.replace(/-/g, '+').replace(/_/g, '/');

    return atob(str);
  }

  private static generateSecret(length: number = 32): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}